import { useState, useMemo } from "react";
import {
  ResponsiveContainer,
  ComposedChart,
  Area,
  Bar,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ReferenceDot,
} from "recharts";
import { Activity, BarChart2, TrendingUp, Eye } from "lucide-react";
import type { RansomwareDailyPoint } from "../../../api/status";
import {
  calculate7DayMovingAverage,
  formatNumber,
  getNiceYAxisTicks,
} from "../../../utils/ransomwareUtils";

interface ActivityChartProps {
  daily: RansomwareDailyPoint[];
  peakDay?: {
    date: string;
    label: string;
    fullDate: string;
    count: number;
  } | null;
  selectedGroup: string | null;
  selectedSector: string | null;
}

export default function ActivityChart({
  daily,
  peakDay,
  selectedGroup,
  selectedSector,
}: ActivityChartProps) {
  const [chartType, setChartType] = useState<"area" | "bar">("area");
  const [showMA, setShowMA] = useState<boolean>(true);

  // Safe array fallback
  const safeDaily = useMemo(() => (Array.isArray(daily) ? daily : []), [daily]);

  // Calculate 7-day moving average
  const maValues = useMemo(() => calculate7DayMovingAverage(safeDaily), [safeDaily]);

  // Combine daily data with calculated MA values
  const chartData = useMemo(() => {
    return safeDaily.map((pt, idx) => ({
      ...pt,
      ma7: maValues[idx] ?? (pt?.count || 0),
      dayIndex: idx,
    }));
  }, [safeDaily, maValues]);

  // YAxis domain & ticks calculation
  const yAxisConfig = useMemo(() => {
    if (safeDaily.length === 0) return { domain: [0, 10] as [number, number], ticks: [0, 2, 4, 6, 8, 10] };
    const counts = safeDaily.map((d) => (typeof d?.count === "number" ? d.count : 0));
    const maxC = counts.length > 0 ? Math.max(...counts) : 10;
    return getNiceYAxisTicks(maxC);
  }, [safeDaily]);

  return (
    <div
      className="rw-activity-chart-panel"
      style={{
        background: "var(--dash-card-bg, rgba(15, 23, 42, 0.6))",
        border: "1px solid var(--dash-card-border, rgba(255, 255, 255, 0.08))",
        borderRadius: 12,
        padding: "20px 24px",
        display: "flex",
        flexDirection: "column",
        gap: 16,
        backdropFilter: "blur(12px)",
      }}
    >
      {/* CHART HEADER & CONTROLS */}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          flexWrap: "wrap",
          gap: 12,
        }}
      >
        <div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <h3
              style={{
                fontSize: "1rem",
                fontWeight: 700,
                color: "var(--dash-text-primary, #f8fafc)",
                margin: 0,
              }}
            >
              Ransomware Activity Timeline
            </h3>
            {(selectedGroup || selectedSector) && (
              <span
                style={{
                  fontSize: "0.72rem",
                  fontWeight: 700,
                  padding: "2px 8px",
                  borderRadius: 999,
                  background: "rgba(0, 102, 224, 0.15)",
                  color: "var(--dash-indigo, #818cf8)",
                  border: "1px solid rgba(0, 102, 224, 0.3)",
                }}
              >
                Filtered by {selectedGroup ? `Group: ${selectedGroup}` : `Sector: ${selectedSector}`}
              </span>
            )}
          </div>
          <p
            style={{
              fontSize: "0.75rem",
              color: "var(--dash-text-muted, #94a3b8)",
              marginTop: 2,
              margin: 0,
            }}
          >
            Daily incident disclosure frequency & rolling trends
          </p>
        </div>

        {/* CONTROLS STRIP */}
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          {/* Moving Avg Toggle */}
          <button
            onClick={() => setShowMA(!showMA)}
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 5,
              fontSize: "0.75rem",
              fontWeight: 600,
              padding: "5px 12px",
              borderRadius: 6,
              background: showMA ? "rgba(0, 102, 224, 0.15)" : "var(--dash-subcard-bg, rgba(255, 255, 255, 0.04))",
              color: showMA ? "var(--dash-indigo, #818cf8)" : "var(--dash-text-muted, #94a3b8)",
              border: `1px solid ${showMA ? "rgba(0, 102, 224, 0.3)" : "var(--dash-card-border, rgba(255, 255, 255, 0.1))"}`,
              cursor: "pointer",
              transition: "all 0.15s ease",
            }}
            title="Toggle 7-day moving average trend line"
            aria-pressed={showMA}
          >
            <TrendingUp size={13} />
            <span>7D Moving Avg</span>
          </button>

          {/* View Mode Segmented Control (Area vs Bar) */}
          <div
            style={{
              display: "inline-flex",
              padding: 2,
              background: "var(--dash-subcard-bg, rgba(255, 255, 255, 0.04))",
              borderRadius: 6,
              border: "1px solid var(--dash-card-border, rgba(255, 255, 255, 0.1))",
            }}
          >
            <button
              onClick={() => setChartType("area")}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 4,
                fontSize: "0.75rem",
                fontWeight: 600,
                padding: "4px 10px",
                borderRadius: 4,
                background: chartType === "area" ? "var(--dash-indigo, #6366F1)" : "transparent",
                color: chartType === "area" ? "#ffffff" : "var(--dash-text-muted, #94a3b8)",
                border: "none",
                cursor: "pointer",
                transition: "all 0.15s ease",
              }}
              title="Area View"
            >
              <Activity size={13} />
              <span>Area</span>
            </button>

            <button
              onClick={() => setChartType("bar")}
              style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 4,
                fontSize: "0.75rem",
                fontWeight: 600,
                padding: "4px 10px",
                borderRadius: 4,
                background: chartType === "bar" ? "var(--dash-indigo, #6366F1)" : "transparent",
                color: chartType === "bar" ? "#ffffff" : "var(--dash-text-muted, #94a3b8)",
                border: "none",
                cursor: "pointer",
                transition: "all 0.15s ease",
              }}
              title="Bar View"
            >
              <BarChart2 size={13} />
              <span>Bars</span>
            </button>
          </div>
        </div>
      </div>

      {/* PEAK ANNOTATION BADGE */}
      {peakDay && peakDay.count > 0 && (
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <span
            style={{
              fontSize: "0.75rem",
              fontWeight: 700,
              padding: "3px 10px",
              borderRadius: 6,
              background: "rgba(245, 158, 11, 0.12)",
              color: "#f59e0b",
              border: "1px solid rgba(245, 158, 11, 0.25)",
              display: "inline-flex",
              alignItems: "center",
              gap: 6,
            }}
          >
            <Eye size={12} /> Peak Surge: <strong>{peakDay.count} disclosures</strong> on {peakDay.fullDate || peakDay.label}
          </span>
        </div>
      )}

      {/* RECHARTS COMPOSED CHART */}
      <div style={{ width: "100%", height: 280, position: "relative" }}>
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart
            data={chartData}
            margin={{ top: 15, right: 15, left: -15, bottom: 0 }}
          >
            <defs>
              <linearGradient id="rwGradientMain" x1="0" y1="0" x2="0" y2="1">
                <stop offset="5%" stopColor="#6366F1" stopOpacity={0.35} />
                <stop offset="95%" stopColor="#6366F1" stopOpacity={0.0} />
              </linearGradient>
            </defs>

            <CartesianGrid
              strokeDasharray="3 3"
              vertical={false}
              stroke="var(--dash-card-border, rgba(255, 255, 255, 0.06))"
            />

            <XAxis
              dataKey="label"
              stroke="var(--dash-text-muted, #64748b)"
              tick={{ fontSize: 12, fill: "var(--dash-text-muted, #94a3b8)", fontVariantNumeric: "tabular-nums" }}
              axisLine={false}
              tickLine={false}
              interval="preserveStartEnd"
              minTickGap={24}
            />

            <YAxis
              domain={yAxisConfig.domain}
              ticks={yAxisConfig.ticks}
              stroke="var(--dash-text-muted, #64748b)"
              tick={{ fontSize: 12, fill: "var(--dash-text-muted, #94a3b8)", fontVariantNumeric: "tabular-nums" }}
              axisLine={false}
              tickLine={false}
            />

            <Tooltip content={<CustomTimelineTooltip />} />

            {/* Main Series: Area (Monotone) or Bars */}
            {chartType === "area" ? (
              <Area
                type="monotone"
                dataKey="count"
                name="Disclosures"
                stroke="#6366F1"
                strokeWidth={2.5}
                fillOpacity={1}
                fill="url(#rwGradientMain)"
                isAnimationActive={true}
                animationDuration={300}
              />
            ) : (
              <Bar
                dataKey="count"
                name="Disclosures"
                fill="#6366F1"
                radius={[4, 4, 0, 0]}
                isAnimationActive={true}
                animationDuration={300}
              />
            )}

            {/* 7-Day Moving Average Line */}
            {showMA && (
              <Line
                type="monotone"
                dataKey="ma7"
                name="7D Moving Avg"
                stroke="#f59e0b"
                strokeWidth={2}
                dot={false}
                activeDot={false}
                isAnimationActive={true}
                animationDuration={400}
              />
            )}

            {/* Annotation marker on Peak Day */}
            {peakDay && peakDay.count > 0 && (
              <ReferenceDot
                x={peakDay.label}
                y={peakDay.count}
                r={5}
                fill="#f59e0b"
                stroke="#ffffff"
                strokeWidth={2}
              />
            )}
          </ComposedChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

{/* CUSTOM TOOLTIP FOR TIMELINE CHART */}
function CustomTimelineTooltip({ active, payload }: any) {
  if (!active || !payload || !payload.length) return null;

  const point: RansomwareDailyPoint = payload[0].payload;
  const count = typeof point?.count === "number" ? point.count : 0;
  const maVal = payload.find((p: any) => p.dataKey === "ma7")?.value;

  return (
    <div
      style={{
        padding: "10px 14px",
        borderRadius: 8,
        background: "rgba(15, 23, 42, 0.95)",
        border: "1px solid rgba(255, 255, 255, 0.15)",
        boxShadow: "0 10px 25px rgba(0, 0, 0, 0.5)",
        color: "#ffffff",
        fontSize: "0.8rem",
        backdropFilter: "blur(8px)",
      }}
    >
      <div style={{ fontSize: "0.7rem", fontWeight: 700, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.05em" }}>
        {point.dayOfWeek || "Date"}
      </div>

      <div style={{ fontSize: "0.88rem", fontWeight: 700, color: "#f8fafc", margin: "2px 0 6px" }}>
        {point.fullDate || point.label}
      </div>

      <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16 }}>
          <span style={{ color: "#cbd5e1" }}>Disclosures:</span>
          <span style={{ fontWeight: 800, color: "#6366F1", fontVariantNumeric: "tabular-nums" }}>
            {formatNumber(count)}
          </span>
        </div>

        {maVal !== undefined && (
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 16, fontSize: "0.75rem" }}>
            <span style={{ color: "#f59e0b" }}>7D Moving Avg:</span>
            <span style={{ fontWeight: 700, color: "#f59e0b", fontVariantNumeric: "tabular-nums" }}>
              {maVal}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
